/**
 * create-bot-wizard.tsx — Tạo bot theo 3 bước, không viết prompt:
 *   1. Chọn vai trò (mẫu): Bán hàng / CSKH / Đơn hàng / Nội bộ
 *   2. Tick nguồn: thư mục tài liệu bán hàng, nhóm kiến thức, kịch bản, kênh
 *   3. Form tính cách (điền sẵn từ mẫu) → Tạo → sang màn train (bước 4: chạy kiểm định)
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, ArrowRight, Check, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { Loading } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  aiKeys, useBotTemplates, useAiScenarios, createBotFromTemplate, CONG_CU_HANH_DONG,
  type BotTemplate, type PersonaForm, type VaiTroBot,
} from '@/hooks/use-ai'
import { useDocFolders } from '@/hooks/use-doc-library'
import { useKnowledgeCategories } from '@/hooks/use-knowledge'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { xepCay } from '@/components/ai/resource-picker-dialog'
import { PersonaFormFields, chotPersonaForm } from '@/components/ai/persona-form-fields'

const TIEN_TO_MAU = 'tpl_'

export function CreateBotWizard({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: tplData, isLoading } = useBotTemplates()
  const { data: folders } = useDocFolders()
  const { data: kbCats } = useKnowledgeCategories()
  const { data: scData } = useAiScenarios()
  const { data: channels } = useZaloAccounts()

  const [buoc, setBuoc] = useState<1 | 2 | 3>(1)
  const [role, setRole] = useState<VaiTroBot | null>(null)
  const [name, setName] = useState('')
  const [folderIds, setFolderIds] = useState<string[]>([])
  const [kbIds, setKbIds] = useState<string[]>([])
  const [scIds, setScIds] = useState<string[] | null>(null) // null = mặc định theo vai trò
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [form, setForm] = useState<PersonaForm | null>(null)
  const [dangTao, setDangTao] = useState(false)

  const templates = tplData?.templates ?? []
  const mau: BotTemplate | undefined = templates.find((t) => t.role === role)

  // Chọn vai trò → điền sẵn tên + form tính cách của mẫu (người dùng sửa sau).
  useEffect(() => {
    if (!mau) return
    setName(mau.name)
    setForm(mau.personaForm)
    setScIds(null)
  }, [mau])

  const cayThuMuc = useMemo(() => xepCay((folders ?? []).map((f) => ({ id: f.id, name: f.name, parentId: f.parentId, count: f.assetCount }))), [folders])
  const cayKb = useMemo(() => xepCay((kbCats ?? []).map((c) => ({ id: c.id, name: c.name, parentId: c.parentId, count: c.entryCount }))), [kbCats])
  const kichBanOrg = (scData?.scenarios ?? []).filter((s) => s.enabled && !s.key.startsWith(TIEN_TO_MAU))
  const tickMang = (xs: string[], id: string) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id])

  async function tao() {
    if (!role || !form) return
    setDangTao(true)
    try {
      const res = await createBotFromTemplate({
        role,
        name: name.trim() || undefined,
        channelAccountIds: channelIds,
        docFolderIds: folderIds,
        knowledgeCategoryIds: kbIds,
        scenarioIds: scIds,
        personaForm: chotPersonaForm(form),
      })
      await qc.invalidateQueries({ queryKey: aiKeys.bots })
      await qc.invalidateQueries({ queryKey: aiKeys.scenarios })
      toast.success('Đã tạo bot — chạy thử và kiểm định ở màn train')
      onClose()
      navigate(`/ai/train/${res.bot.id}`)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setDangTao(false)
    }
  }

  const tieuDeBuoc = ['Chọn vai trò', 'Chọn nguồn tri thức', 'Tính cách'][buoc - 1]

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Tạo bot AI · Bước {buoc}/3 — {tieuDeBuoc}
          </DialogTitle>
          <DialogDescription>
            {buoc === 1 && 'Mỗi vai trò có sẵn kịch bản, luật chuyển nhân viên và công cụ phù hợp. Sửa từng phần được sau.'}
            {buoc === 2 && 'Không tick gì = bot đọc toàn bộ. Tick thư mục cha là gồm cả thư mục con.'}
            {buoc === 3 && 'Điền form, hệ thống tự viết prompt tính cách — không cần biết viết prompt.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? <Loading label="Đang tải mẫu…" /> : (
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {buoc === 1 && (
              <div className="grid gap-2 sm:grid-cols-2">
                {templates.map((t) => (
                  <button
                    key={t.role} type="button" onClick={() => setRole(t.role)}
                    className={`rounded-lg border p-3 text-left transition-colors ${role === t.role ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{t.emoji}</span>
                      <span className="font-semibold">{t.name}</span>
                      {role === t.role && <Check className="ml-auto h-4 w-4 text-primary" />}
                    </div>
                    <p className="mt-1 text-xs font-medium text-primary">{t.muctieu}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t.moTa}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {t.kichBan.map((k) => <Badge key={k.key} variant="outline" className="text-[10px]">{k.name}</Badge>)}
                      {t.tatCongCu.map((c) => (
                        <Badge key={c} variant="secondary" className="text-[10px]">
                          không {CONG_CU_HANH_DONG.find((x) => x.name === c)?.label.toLowerCase() ?? c}
                        </Badge>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {buoc === 2 && mau && (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Tên bot</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={mau.name} />
                </div>

                {mau.traCuu.sanPham && (
                  <NhomTick
                    tieuDe="Thư mục tài liệu bán hàng" ghiChu={folderIds.length === 0 ? 'toàn bộ' : `${folderIds.length} thư mục`}
                    items={cayThuMuc} selected={folderIds} onToggle={(id) => setFolderIds((x) => tickMang(x, id))}
                  />
                )}
                {mau.traCuu.kienThuc && (
                  <NhomTick
                    tieuDe="Nhóm kiến thức" ghiChu={kbIds.length === 0 ? 'toàn bộ' : `${kbIds.length} nhóm`}
                    items={cayKb} selected={kbIds} onToggle={(id) => setKbIds((x) => tickMang(x, id))}
                    trong="Chưa có nhóm kiến thức — tạo ở tab Kiến thức để giới hạn theo nhóm."
                  />
                )}

                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <Label className="text-xs">Kịch bản của tổ chức</Label>
                    <span className="text-[11px] text-muted-foreground">
                      {scIds === null ? (role === 'sales' ? 'mặc định: tất cả' : 'mặc định: chỉ kịch bản của mẫu') : `${scIds.length} đã chọn`}
                    </span>
                  </div>
                  <p className="mb-1 text-[11px] text-muted-foreground">
                    Kịch bản đi kèm mẫu ({mau.kichBan.map((k) => k.name).join(', ') || 'không có'}) luôn được gắn.
                  </p>
                  <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border p-1">
                    {kichBanOrg.length === 0 && <p className="p-2 text-xs text-muted-foreground">Tổ chức chưa có kịch bản nào.</p>}
                    {kichBanOrg.map((s) => {
                      const macDinh = scIds === null && role === 'sales'
                      const checked = macDinh || (scIds ?? []).includes(s.id)
                      return (
                        <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
                          <input
                            type="checkbox" checked={checked}
                            onChange={() => {
                              const nen = scIds ?? (macDinh ? kichBanOrg.map((x) => x.id) : [])
                              setScIds(tickMang(nen, s.id))
                            }}
                          />
                          <span className="min-w-0 flex-1 truncate">{s.name}</span>
                          <Badge variant="outline" className="text-[10px]">{s.loadMode === 'always' ? 'luôn' : 'theo ngữ cảnh'}</Badge>
                        </label>
                      )
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <Label className="text-xs">Kênh bot phục vụ</Label>
                    <span className="text-[11px] text-muted-foreground">{channelIds.length === 0 ? 'gán sau cũng được' : `${channelIds.length} kênh`}</span>
                  </div>
                  <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-md border p-1">
                    {(channels ?? []).length === 0 && <p className="p-2 text-xs text-muted-foreground">Chưa kết nối kênh nào.</p>}
                    {(channels ?? []).map((ch) => (
                      <label key={ch.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
                        <input type="checkbox" checked={channelIds.includes(ch.id)} onChange={() => setChannelIds((x) => tickMang(x, ch.id))} />
                        <span className="min-w-0 flex-1 truncate">{ch.displayName || 'Kênh chưa đặt tên'}</span>
                        {ch.phone && <span className="text-[10px] tabular-nums text-muted-foreground">{ch.phone}</span>}
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">Mỗi kênh chỉ thuộc một bot — gán ở đây sẽ gỡ khỏi bot khác.</p>
                </div>
              </div>
            )}

            {buoc === 3 && role && form && (
              <PersonaFormFields value={form} onChange={setForm} role={role} />
            )}
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-3">
          <Button variant="ghost" onClick={() => (buoc === 1 ? onClose() : setBuoc((b) => (b - 1) as 1 | 2 | 3))} disabled={dangTao}>
            <ArrowLeft className="h-4 w-4" /> {buoc === 1 ? 'Huỷ' : 'Quay lại'}
          </Button>
          {buoc < 3 ? (
            <Button onClick={() => setBuoc((b) => (b + 1) as 1 | 2 | 3)} disabled={!role || (buoc === 2 && !mau)}>
              Tiếp <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={() => void tao()} disabled={dangTao || !form}>
              <Check className="h-4 w-4" /> {dangTao ? 'Đang tạo…' : 'Tạo bot'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function NhomTick({ tieuDe, ghiChu, items, selected, onToggle, trong }: {
  tieuDe: string
  ghiChu: string
  items: ReturnType<typeof xepCay>
  selected: string[]
  onToggle: (id: string) => void
  trong?: string
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <Label className="text-xs">{tieuDe}</Label>
        <span className="text-[11px] text-muted-foreground">{ghiChu}</span>
      </div>
      <div className="max-h-44 space-y-0.5 overflow-y-auto rounded-md border p-1">
        {items.length === 0 && <p className="p-2 text-xs text-muted-foreground">{trong ?? 'Chưa có mục nào.'}</p>}
        {items.map((c) => {
          const chaDaTick = c.toTien.some((t) => selected.includes(t))
          return (
            <label
              key={c.id} style={{ paddingLeft: 8 + c.depth * 18 }}
              className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted ${chaDaTick ? 'opacity-60' : ''}`}
            >
              <input type="checkbox" checked={selected.includes(c.id) || chaDaTick} disabled={chaDaTick} onChange={() => onToggle(c.id)} />
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
              {chaDaTick ? <span className="text-[10px] text-muted-foreground">đã gồm</span>
                : typeof c.count === 'number' && <span className="text-[10px] tabular-nums text-muted-foreground">{c.count}</span>}
            </label>
          )
        })}
      </div>
    </div>
  )
}
